import * as React from 'react';
import cn from 'classnames';
import fileSize from 'filesize';
import {FormattedMessage} from 'react-intl';
import {ItemType} from './FileViewer';
import {SelectButton} from './SelectButton';
import HumanReadableTime from '../../StudioHumanReadableTime/HumanReadableTime';
import {
  StudioVirtualizedTableCell,
  StudioVirtualizedTableNumericCell,
  TableCellProps,
  TableHeaderCell,
  TableHeadersProps,
  useStudioVirtualizedTableRow,
} from '../../StudioVirtualizedTable/TableComponents';
import StudioVirtualizedTable, {
  StudioVirtualizedTableProps,
} from '../../StudioVirtualizedTable/StudioVirtualizedTable';
import {StudioFileItemParamProps} from '.';
import {BinaryListRenderer} from '../Renderer';
import {TableCell, TableRow} from '@material-ui/core';
import {SCROLL_DEBOUNCE_TIME, THUMBNAIL_SIZE_LIST} from '../const';
import {Skeleton} from '@material-ui/lab';
import {StudioDatasetViewerProps} from '../StudioDatasetViewer';
import {ListOnItemsRenderedProps} from 'react-window';
import {debounce} from 'lodash';
import {get} from 'lodash';
import './ListViewer.scss';

export const ROW_SIZE = 55;

export const getRenderer = (item: any, renderer: React.ReactNode) => {
  if (item) {
    return renderer;
  }
  return <Skeleton className="skeleton" />;
};

export const FilenameCell = () => {
  const {item} = useStudioVirtualizedTableRow();
  return (
    <StudioVirtualizedTableCell className="filename_cell" style={{height: ROW_SIZE}}>
      {getRenderer(item, item?.filename)}
    </StudioVirtualizedTableCell>
  );
};

export const ImageDimensionCell = () => {
  const {item} = useStudioVirtualizedTableRow();
  const height = item?.height;
  const width = item?.width;

  return (
    <StudioVirtualizedTableNumericCell style={{flexBasis: 100, height: ROW_SIZE}}>
      {getRenderer(
        item,
        <span>
          {height !== undefined && width !== undefined ? `${width} x ${height}` : ''}
        </span>
      )}
    </StudioVirtualizedTableNumericCell>
  );
};

export const DateCell = ({field, rowSize = ROW_SIZE, className}: TableCellProps) => {
  const {item, columnDefObj} = useStudioVirtualizedTableRow();
  const {width} = get(columnDefObj, field);
  const date = get(item, field) as Date;
  const style = width ? {flexBasis: width} : {flex: 1, display: 'block'};
  return (
    <StudioVirtualizedTableCell style={{...style, height: rowSize}} className={className}>
      {getRenderer(
        item,
        date ? (
          <HumanReadableTime date={date} utc={true} />
        ) : (
          <FormattedMessage id="general.na.short" />
        )
      )}
    </StudioVirtualizedTableCell>
  );
};

export const DynamicCell = ({field, rowSize = ROW_SIZE, className}: TableCellProps) => {
  const {item, columnDefObj} = useStudioVirtualizedTableRow();
  const {width} = get(columnDefObj, field);
  const style = width !== undefined ? {flexBasis: width} : {flex: 1, display: 'block'};
  return (
    <StudioVirtualizedTableCell
      style={{...style, height: rowSize}}
      className={`dynamic_cell ${className}`}
    >
      {getRenderer(item, get(item, field))}
    </StudioVirtualizedTableCell>
  );
};

export const FileSizeCell = ({field, rowSize = ROW_SIZE, className}: TableCellProps) => {
  const {item, columnDefObj} = useStudioVirtualizedTableRow();
  const {width} = get(columnDefObj, field);
  const style = width !== undefined ? {flexBasis: width} : {flex: 1, display: 'block'};
  const value = get(item, field);
  return (
    <StudioVirtualizedTableNumericCell
      style={{...style, height: rowSize}}
      className={className}
    >
      {getRenderer(
        item,
        value !== undefined ? fileSize(value) : <FormattedMessage id="general.na.short" />
      )}
    </StudioVirtualizedTableNumericCell>
  );
};

const thumbnailSizeParam = `previewDimensions=${THUMBNAIL_SIZE_LIST}x${THUMBNAIL_SIZE_LIST}`;
export const ImageCell = ({
  selections = [],
  onSelect = () => {},
  onClick = () => {},
}: Pick<ListViewerProps, 'selections' | 'onSelect' | 'onClick'>) => {
  const {item} = useStudioVirtualizedTableRow();
  return (
    <StudioVirtualizedTableCell
      style={{flexBasis: 55, height: ROW_SIZE, margin: 0, padding: 0}}
      className="image_cell"
      onClick={() => onClick(item)}
    >
      {getRenderer(
        item,
        <BinaryListRenderer url={item?.file || `${item?.url}&${thumbnailSizeParam}`} />
      )}
    </StudioVirtualizedTableCell>
  );
};

export const SelectorCell = ({
  onSelect = () => {},
  selections = [],
}: Pick<ListViewerProps, 'selections' | 'onSelect'>) => {
  const {item} = useStudioVirtualizedTableRow();
  return (
    <StudioVirtualizedTableCell
      style={{flexBasis: 55, height: ROW_SIZE}}
      className="select_cell"
    >
      {getRenderer(
        item,
        <SelectButton
          checked={selections.includes(item?.id)}
          onChange={() => onSelect(item)}
          className="list-view_selector"
        />
      )}
    </StudioVirtualizedTableCell>
  );
};

export const LabelCell = () => DynamicCell({field: 'label'});
export const LastEditedCell = () => DateCell({field: 'lastEdited'});

export type ListViewerProps = {
  items: ItemType[];
  selections?: string[];
  imageRenderer?: (config: StudioFileItemParamProps) => React.ReactNode;
  onSelect?(items: any): void;
  totalRowCount?: number;
  onSelectAll?(value: boolean, arr: any[]): void;
  onScrollStop?: (args: ListOnItemsRenderedProps) => void;
} & Pick<
  StudioVirtualizedTableProps,
  | 'showHeaders'
  | 'rowSize'
  | 'columns'
  | 'sort'
  | 'sortDir'
  | 'onSort'
  | 'loadMore'
  | 'totalData'
> &
  Pick<StudioDatasetViewerProps, 'busyItems' | 'onClick' | 'allowSelection'>;

export const ListViewerVirtualized = ({
  columns,
  items = [],
  rowSize,
  imageRenderer = () => <></>,
  onSelect = () => {},
  onClick = () => {},
  selections = [],
  totalRowCount = 0,
  onSelectAll = () => {},
  onScrollStop = () => {},
  allowSelection = true,

  ...rest
}: ListViewerProps) => {
  const mainHeadersColumns = columns.slice(1); //remove the selector column since we will add it manually with custom behaviors

  const debouncedOnScrollStop = debounce(
    (args: ListOnItemsRenderedProps) => onScrollStop(args),
    SCROLL_DEBOUNCE_TIME
  );

  const onItemsRendered = (args: ListOnItemsRenderedProps) => {
    debouncedOnScrollStop(args);
  };

  return (
    <StudioVirtualizedTable
      columns={columns}
      data={items}
      rowSize={ROW_SIZE}
      className="list-view"
      onItemsRendered={onItemsRendered}
      {...rest}
      headerRenderer={({classes, columns, ...rest}: TableHeadersProps) => {
        //  Custom header | Adding Select All functionality
        const numSelected = selections.length;
        // Still unsure which total we use to dtermine the selectAll state tocal vs data row count
        const rowCount = items.length;
        return (
          <TableRow className={cn(classes.row, classes.headerRow)}>
            {allowSelection && (
              <TableCell className="select_cell">
                <SelectButton
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  // Pass empty since list select all has different handling
                  onChange={(e, val) => onSelectAll(val, [])}
                  inputProps={{'aria-label': 'select all items'}}
                />
              </TableCell>
            )}
            {mainHeadersColumns.map((column, colIndex) => {
              return (
                <TableHeaderCell
                  column={column}
                  colIndex={colIndex}
                  classes={classes}
                  columns={columns}
                  {...rest}
                />
              );
            })}
          </TableRow>
        );
      }}
    >
      {/* Row */}
      {allowSelection && <SelectorCell onSelect={onSelect} selections={selections} />}
      <ImageCell onClick={onClick} />
      <FilenameCell />
      <LabelCell />
      <LastEditedCell />
      <ImageDimensionCell />
      <FileSizeCell field="size" />
    </StudioVirtualizedTable>
  );
};
